import type { Ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'
import {
  POOL_ROOM_ID,
  UnsupportedActionError,
  pickNextRoomAccent,
} from '../../lib/boardInvariants'
import {
  PlayerAddDocument,
  PlayerRemoveDocument,
} from '@/shared/api/gql/graphql'
import {
  RoomAddDocument,
  RoomRemoveDocument,
} from '../../lib/handWrittenGraphql'
import type { RoomAddMutation } from '../../lib/handWrittenGraphql'
import type { BoardState, Player, PlayerId, PlayerGroup, PlayerNameMode, Room, RoomId, ViewMode } from '../../types'
import { GuildMembersDocument } from '@/shared/api/gql/graphql'
import type { GuildMembersQuery } from '@/shared/api/gql/graphql'

// Local alias so the dispatch call sites (`apolloClient.mutate(...)`) keep
// their existing shape — Phase B source-grep tests in
// `lib/__tests__/playerTags.test.ts` pin that identifier against the
// document dispatch body. The alias resolves through the canonical
// `resolveClient` helper, so this is the same observable client at
// runtime.
const apolloClient = resolveClient()

function snowflakeVar(s: string): string {
  return s
}

export interface BoardActionsCtx {
  readonly activeDiscordGuildId: Readonly<Ref<string>>
  readonly error: Ref<string | null>
  readonly players: Ref<ReadonlyArray<Player>>
  readonly groups: Ref<ReadonlyArray<PlayerGroup>>
  readonly rooms: Ref<ReadonlyArray<Room>>
  readonly viewMode: Ref<ViewMode>
  readonly playerNameMode: Ref<PlayerNameMode>
  readonly numericRoomId: Map<string, number>
  /** Explicit manual recovery action; never called by a mutation. */
  readonly fetchBoard: () => Promise<void>
}

export interface BoardActionsApi {
  readonly addRoom: (name?: string) => Promise<RoomId>
  readonly removeRoom: (roomId: RoomId) => void
  readonly renameRoom: (roomId: RoomId, name: string) => Promise<void>
  readonly addNewPlayer: () => Promise<Player | null>
  readonly addGuildMember: (discordUserId: string) => Promise<Player | null>
  readonly removePlayer: (playerId: PlayerId) => void
  /**
   * One row of the guild roster as the officer panel renders it. The
   * raw GraphQL `node.discordUserId` is a number (a Discord snowflake
   * truncated through JS `Number`); we coerce to string here at the
   * store boundary so the consumer doesn't have to. Same coercion as
   * `setGuildOfficer` in `stores/session.ts`.
   */
  readonly fetchOfficerRoster: (
    dbGuildId: number,
  ) => Promise<ReadonlyArray<OfficerRosterMember>>
  readonly reset: () => void
  readonly exportSnapshot: () => BoardState
}

/**
 * Shape returned by `fetchOfficerRoster`. Kept narrow — only the
 * fields `OfficerRosterPanel.vue` actually consumes today.
 */
export interface OfficerRosterMember {
  readonly discordUserId: string
  readonly username: string
  readonly isOfficer: boolean
}

export function useBoardActions(ctx: BoardActionsCtx): BoardActionsApi {
  function addRoom(name?: string): Promise<RoomId> {
    const trimmed = (name ?? '').trim()
    if (trimmed === '') {
      return Promise.resolve('')
    }
    const accent = pickNextRoomAccent(ctx.rooms.value)
    return (async (): Promise<RoomId> => {
      try {
        const res = await apolloClient.mutate<RoomAddMutation>({
          mutation: RoomAddDocument,
          variables: {
            guildId: ctx.activeDiscordGuildId.value,
            name: trimmed,
            accent,
          } as never,
        })
        const newNumericId = res.data?.roomAdd ?? ''
        if (newNumericId === '') return ''
        // The stable id is installed by the authoritative subscription
        // snapshot. Returning the server id keeps the legacy method's
        // acknowledgement shape without issuing a second Board query.
        return newNumericId
      } catch (e: unknown) {
        ctx.error.value = e instanceof Error ? e.message : String(e)
        return ''
      }
    })()
  }

  function removeRoom(roomId: RoomId): void {
    if (roomId === POOL_ROOM_ID) return
    const numericId = ctx.numericRoomId.get(roomId)
    if (numericId === undefined) return
    void runInternal(RoomRemoveDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      roomId: numericId,
    })
  }

  function renameRoom(_roomId: RoomId, _name: string): Promise<void> {
    const err = new UnsupportedActionError('renameRoom')
    ctx.error.value = err.message
    return Promise.reject(err)
  }

  function addNewPlayer(): Promise<Player | null> {
    const message = 'Players are added from the synced Discord roster (bot /sync_members).'
    ctx.error.value = message
    return Promise.reject(new UnsupportedActionError('addNewPlayer'))
  }

  async function addGuildMember(discordUserId: string): Promise<Player | null> {
    try {
      const result = await apolloClient.mutate<{ playerAdd: string }>({
        mutation: PlayerAddDocument,
        variables: {
          guildId: snowflakeVar(ctx.activeDiscordGuildId.value),
          discordUserId: snowflakeVar(discordUserId),
        },
      })
      const newId = result.data?.playerAdd
      return newId ? (ctx.players.value.find((p) => p.id === newId) ?? null) : null
    } catch (e: unknown) {
      ctx.error.value = e instanceof Error ? e.message : String(e)
      return null
    }
  }

  function removePlayer(playerId: PlayerId): void {
    void runInternal(PlayerRemoveDocument, {
      guildId: ctx.activeDiscordGuildId.value,
      playerId: Number(playerId),
    })
  }

  function reset(): void {
    void ctx.fetchBoard()
  }

  /**
   * Officer-panel roster fetch — owns the GraphQL dispatch for the
   * guild roster (used by `OfficerRosterPanel.vue`). Lifted out of the
   * component in Phase B (G6) so the slice is the only layer that
   * touches Apollo. Returns the row list mapped through the
   * `OfficerRosterMember` shape (string-form `discordUserId` so the
   * snowflake round-trips losslessly); the caller is responsible for
   * its own loading / error UI state. Errors are also written to the
   * shared `ctx.error` ref so the global error banner catches them
   * alongside every other dispatch failure.
   */
  async function fetchOfficerRoster(
    dbGuildId: number,
  ): Promise<ReadonlyArray<OfficerRosterMember>> {
    try {
      const res = await apolloClient.query<GuildMembersQuery>({
        query: GuildMembersDocument,
        variables: { guildId: dbGuildId },
        fetchPolicy: 'network-only',
      })
      const edges = res.data?.guildMembers.edges ?? []
      return edges.map((edge) => ({
        discordUserId: String(edge.node.discordUserId),
        username: edge.node.username,
        isOfficer: edge.node.isOfficer,
      }))
    } catch (e: unknown) {
      ctx.error.value = e instanceof Error ? e.message : String(e)
      return []
    }
  }

  function exportSnapshot(): BoardState {
    return {
      players: ctx.players.value,
      groups: ctx.groups.value,
      rooms: ctx.rooms.value,
      viewMode: ctx.viewMode.value,
      playerNameMode: ctx.playerNameMode.value,
    }
  }

  async function runInternal(
    mutation: unknown,
    variables: Record<string, unknown>,
  ): Promise<void> {
    try {
      await apolloClient.mutate({ mutation: mutation as never, variables })
      ctx.error.value = null
    } catch (e: unknown) {
      ctx.error.value = e instanceof Error ? e.message : String(e)
    }
  }

  return {
    addRoom,
    removeRoom,
    renameRoom,
    addNewPlayer,
    addGuildMember,
    removePlayer,
    fetchOfficerRoster,
    reset,
    exportSnapshot,
  }
}
